import { assertUnreachable, isDefined, isNotDefined } from '@sgme/fp';
import { format } from 'date-fns';
import { v4 as uuid } from 'uuid';

import { selectCurrencyPairData } from '@/api/config/selectors.ts';
import { espApi } from '@/store/esp/api';
import { espStreamSubscribed } from '@/store/esp/slice.ts';
import { algoOrderApi } from '@/store/orders/algoOrders/api';
import { LIQUIDITY_POOLS } from '@/store/orders/algoOrders/state.ts';
import { callOrderApi } from '@/store/orders/call/api';
import { fixingOrderApi } from '@/store/orders/fixing/api';
import { selectFixingTile } from '@/store/orders/fixing/selectors.ts';
import { ordersSlice } from '@/store/orders/slice.ts';
import type { OrderType } from '@/store/orders/state.ts';
import { stopLossOrderApi } from '@/store/orders/stopLoss/api';
import { takeProfitOrderApi } from '@/store/orders/takeProfitOrder/api';
import type { ClientWay } from '@/store/orders/types.ts';
import { createAppAsyncThunk } from '@/store/storeMethods.ts';
import { selectActiveTabId } from '@/store/tabs/slice.ts';
import { addTile } from '@/store/tiles/slice.ts';
import { getUserPreferences } from '@/store/userPreferences/slice.ts';
import type { CurrencyIndex } from '@/utils/currencies/types.ts';
import { normalizeEmailsList } from '@/utils/notificationEmails.ts';

interface CreateOrderTileParams {
  clientData: { id: number; isOverriddenId: boolean };
  clientWay: ClientWay;
  currencyPair: { currency1: string; currency2: string };
  notional: { amount: number | undefined; currencyIndex: CurrencyIndex | undefined };
  orderType: OrderType;
  position?: { x: number; y: number };
}

export const createOrderTileFromCashTile = createAppAsyncThunk<void, CreateOrderTileParams>(
  'order/createOrderTile',
  async (
    { orderType, position = { x: 0, y: 0 }, clientData, clientWay, currencyPair, notional },
    { dispatch, getState },
  ) => {
    const currentState = getState();

    const newOrderTileId = uuid();
    const currentTabId = selectActiveTabId(currentState);
    const userPreferences = getUserPreferences(currentState);

    const preferredEmails = userPreferences.splitNotifications
      ? userPreferences.splitNotificationsEmailsOrders
      : userPreferences.emails;

    dispatch(
      addTile({
        tile: {
          tileId: newOrderTileId,
          tabId: currentTabId,
          productType: orderType,
          x: position.x,
          y: position.y,
          h: 1,
          w: 1,
        },
        userPreferences,
        sessionId: uuid(),
      }),
    );

    const stateWithNewTile = getState();
    const newOrderTile = stateWithNewTile.orders[newOrderTileId];
    if (isNotDefined(newOrderTile)) {
      return;
    }

    if (clientData.isOverriddenId) {
      dispatch(ordersSlice.actions.changeClientId({ tileId: newOrderTileId, clientId: clientData.id }));
    }

    const { currency1, currency2 } = currencyPair;
    const isNotDeliverableCurrencyPair =
      selectCurrencyPairData(stateWithNewTile, `${currency1}/${currency2}`)?.notDeliverable ?? false;

    switch (orderType) {
      case 'TakeProfitOrder': {
        dispatch(
          takeProfitOrderApi.endpoints.validateTakeProfit.initiate({
            tileId: newOrderTileId,
            clientId: clientData.id,
            currency1,
            currency2,
            expiry: { type: 'GoodTillCancelled' },
            way: clientWay,
            notional: notional.amount,
            notionalCurrencyIndex: notional.currencyIndex ?? 1,
            notificationEmails: {
              isOverridenEmails: false,
              emails: normalizeEmailsList(preferredEmails),
            },
            localTimeWithOffSet: 0,
          }),
        );

        break;
      }
      case 'StopLossOrder': {
        dispatch(
          stopLossOrderApi.endpoints.validateStopLoss.initiate({
            tileId: newOrderTileId,
            clientId: clientData.id,
            currency1,
            currency2,
            way: clientWay,
            notional: notional.amount,
            notionalCurrencyIndex: notional.currencyIndex ?? 1,
            trigger: 'Spot',
            expiry: { type: 'GoodTillCancelled' },
            notificationEmails: {
              isOverridenEmails: false,
              emails: normalizeEmailsList(preferredEmails),
            },
          }),
        );

        break;
      }
      case 'CallOrder': {
        dispatch(
          callOrderApi.endpoints.validateCallOrder.initiate({
            tileId: newOrderTileId,
            currency1,
            currency2,
            clientId: clientData.id,
            expiry: { type: 'GoodTillCancelled' },
            notificationEmails: {
              isOverridenEmails: false,
              emails: normalizeEmailsList(preferredEmails),
            },
          }),
        );

        break;
      }
      case 'FixingOrder': {
        dispatch(
          fixingOrderApi.endpoints.validateFixing.initiate({
            tileId: newOrderTileId,
            currency1,
            currency2,
            clientId: clientData.id,
            way: clientWay,
            notional: notional.amount,
            notionalCurrencyIndex: notional.currencyIndex ?? 1,
            fixingReference: '',
            fixingPriceType: 'MID',
            fixingMarginType: 'MarginInBps',
            notificationEmails: {
              isOverridenEmails: false,
              emails: normalizeEmailsList(preferredEmails),
            },
          }),
        )
          .unwrap()
          .then((response) => {
            const updatedProperties = selectFixingTile(getState(), newOrderTileId);
            if (isNotDefined(updatedProperties)) {
              return;
            }

            const fixingReference = response.fixingTypes.length > 0 ? response.fixingTypes[0] : undefined;
            dispatch(
              fixingOrderApi.endpoints.validateFixing.initiate({
                tileId: newOrderTileId,
                currency1,
                currency2,
                clientId: clientData.id,
                way: updatedProperties.properties.clientWay,
                notional: updatedProperties.properties.notional,
                notionalCurrencyIndex: updatedProperties.properties.notionalCurrencyIndex ?? 1,
                fixingDateUtc: updatedProperties.properties.fixingDateUtc,
                expiryDay: updatedProperties.properties.expiryDay,
                fixingReference: fixingReference ?? updatedProperties.properties.fixingReference,
                fixingPriceType: updatedProperties.properties.fixingPriceType,
                fixingTime: updatedProperties.properties.fixingTime,
                fixingPlace: updatedProperties.properties.fixingPlace,
                commercialMargin: updatedProperties.properties.commercialMargin,
                fixingMarginType: updatedProperties.properties.fixingMarginType,
                notificationEmails: {
                  isOverridenEmails: isDefined(updatedProperties.properties.notificationEmails),
                  emails: normalizeEmailsList(updatedProperties.properties.notificationEmails ?? []),
                },
                clockOffset: updatedProperties.properties.clockOffset,
              }),
            );
          });
        break;
      }
      case 'Twap+Order': {
        dispatch(
          algoOrderApi.endpoints.validateAlgo.initiate({
            orderType: 'Twap+Order',
            tileId: newOrderTileId,
            currency1,
            currency2,
            clientId: clientData.id,
            way: clientWay,
            notional: notional.amount,
            notionalCurrencyIndex: notional.currencyIndex ?? 1,
            isNdf: isNotDeliverableCurrencyPair,
            clippingMode: 'Automatic',
            spreadCapture: true,
            randomize: true,
            liquidityPool: [...LIQUIDITY_POOLS],
            expiryDate: { goodTillDate: true },
            startDate: { isNow: true },
            notificationEmails: {
              isOverridenEmails: false,
              emails: normalizeEmailsList(preferredEmails),
            },
          }),
        );

        break;
      }
      case 'NightjarOrder': {
        dispatch(
          algoOrderApi.endpoints.validateAlgo.initiate({
            orderType: 'NightjarOrder',
            tileId: newOrderTileId,
            currency1,
            currency2,
            clientId: clientData.id,
            way: clientWay,
            notional: notional.amount,
            notionalCurrencyIndex: notional.currencyIndex ?? 1,
            isNdf: isNotDeliverableCurrencyPair,
            liquidityPool: [...LIQUIDITY_POOLS],
            alphaSeeker: false,
            speed: 'Normal',
            expiryDate: { goodTillDate: true },
            startDate: { isNow: true },
            notificationEmails: {
              isOverridenEmails: false,
              emails: normalizeEmailsList(preferredEmails),
            },
          }),
        );

        break;
      }
      case 'FalconOrder': {
        dispatch(
          algoOrderApi.endpoints.validateAlgo.initiate({
            orderType: 'FalconOrder',
            tileId: newOrderTileId,
            currency1,
            currency2,
            clientId: clientData.id,
            way: clientWay,
            notional: notional.amount,
            notionalCurrencyIndex: notional.currencyIndex ?? 1,
            isNdf: isNotDeliverableCurrencyPair,
            liquidityPool: [...LIQUIDITY_POOLS],
            expiryDate: { goodTillDate: true },
            startDate: { isNow: true },
            speed: 'Normal',
            notificationEmails: {
              isOverridenEmails: false,
              emails: normalizeEmailsList(preferredEmails),
            },
          }),
        );

        break;
      }
      default:
        assertUnreachable(orderType, `Unknow order type: ${orderType}`);
    }

    const tieringResult = await dispatch(
      espApi.endpoints.getEspTiering.initiate({
        currencyPair: `${currency1}/${currency2}`,
        clientId: clientData.id,
        spotDate: format(new Date(), 'yyyy-MM-dd'),
      }),
    ).unwrap();

    if ('error' in tieringResult) {
      return;
    }

    dispatch(
      espStreamSubscribed({
        currencyPair: `${currency1}/${currency2}`,
        tiering: tieringResult.tiering,
        tileId: newOrderTileId,
        bdrId: clientData.id,
      }),
    );
  },
);
